import type { Asset } from "rujira.js";
import type { useTranslation } from "rujira.ui";
import {
  assetToLendUrlSegment,
  assetToTradeUrlSegment,
} from "../../services/assetUrl";
import {
  canonicalAssetSymbol,
  isSameCanonicalAsset,
} from "../../services/canonicalAsset";
import { stakeRoute } from "../../staking/routes";
import { formatBigintPercent } from "../bigint";
import type { TransferSuccessRecommendationsQuery } from "./__generated__/TransferSuccessRecommendationsQuery.graphql";

export type TransferSuccessRecommendation = {
  kind: "automated" | "lending" | "staking" | "borrow" | "trade" | "ruji";
  href: string;
  title: string;
  description: string;
  apr?: string;
  aprLabel?: "upTo" | "current" | "borrow";
};

type Translate = ReturnType<typeof useTranslation>["t"];
type Range = NonNullable<
  NonNullable<
    NonNullable<
      TransferSuccessRecommendationsQuery["response"]["finV3"]
    >["ranges"]
  >["edges"]
>[number];
type Market = NonNullable<
  NonNullable<TransferSuccessRecommendationsQuery["response"]["finV2"]>["edges"]
>[number];

const MIN_DISPLAY_APR = 5n * 10n ** 9n;

export const buildRecommendations = (
  asset: Asset,
  data: TransferSuccessRecommendationsQuery["response"],
  t: Translate
): TransferSuccessRecommendation[] => {
  const symbol = canonicalAssetSymbol(asset.metadata.symbol);
  const ranges = (data.finV3?.ranges?.edges || []).flatMap((edge) =>
    edge?.node?.pair ? [edge] : []
  );
  const markets = (data.finV2?.edges || []).flatMap((edge) =>
    edge?.node ? [edge] : []
  );
  const automatedRange = findBestAutomatedRange(symbol, ranges);
  const tradeMarket = findBestTradeMarket(symbol, markets);
  const choices = [
    automatedRecommendation(symbol, t, automatedRange),
    lendingRecommendation(asset, data, t),
    stakingRecommendation(symbol, data, t),
    borrowRecommendation(asset, data, t),
    tradeRecommendation(symbol, t, tradeMarket),
    rujiRecommendation(data, t),
  ].filter((item): item is TransferSuccessRecommendation => Boolean(item));

  return choices
    .filter(
      (item, index) =>
        choices.findIndex((candidate) => candidate.href === item.href) === index
    )
    .slice(0, 3);
};

const findBestAutomatedRange = (
  symbol: string,
  ranges: readonly Range[]
): Range | undefined =>
  // The query requests APR descending, so the first matching range is best.
  ranges.find((edge) => {
    const pair = edge?.node?.pair;
    if (!pair) return false;
    const base = canonicalAssetSymbol(pair.assetBase.metadata.symbol);
    const quote = canonicalAssetSymbol(pair.assetQuote.metadata.symbol);
    return symbol === "USDC" || symbol === "USDT"
      ? [base, quote].includes("USDC") && [base, quote].includes("USDT")
      : base === symbol || quote === symbol;
  });

const findBestTradeMarket = (
  symbol: string,
  markets: readonly Market[]
): Market | undefined =>
  // The query requests volume descending, so the first matching market is best.
  markets.find((edge) => {
    const pair = edge?.node;
    if (!pair) return false;
    return [pair.assetBase, pair.assetQuote].some(
      (asset) => canonicalAssetSymbol(asset.metadata.symbol) === symbol
    );
  });

const automatedRecommendation = (
  symbol: string,
  t: Translate,
  range?: Range
): TransferSuccessRecommendation | undefined => {
  const pair = range?.node?.pair;
  const stablePair = symbol === "USDC" || symbol === "USDT";
  // Never link to a market we could not find: only recommend Automated
  // Trading when a matching range exists (stablecoins always have USDT/USDC).
  if (!pair && !stablePair) return undefined;
  const routeBase = stablePair
    ? "USDT"
    : assetToTradeUrlSegment(pair!.assetBase);
  const routeQuote = stablePair
    ? "USDC"
    : assetToTradeUrlSegment(pair!.assetQuote);
  return {
    kind: "automated",
    href: `/trade/${routeBase}/${routeQuote}?type=automated&strategy=ccl`,
    title: t("transactionExploreAutomatedTitle", { symbol }),
    description: t("transactionExploreAutomatedDescription", { symbol }),
    apr: displayApr(range?.node?.analytics?.apr),
    aprLabel: "upTo",
  };
};

const lendingRecommendation = (
  asset: Asset,
  data: TransferSuccessRecommendationsQuery["response"],
  t: Translate
): TransferSuccessRecommendation | undefined => {
  const vault = data.strategies?.edges?.find(
    (edge) =>
      edge?.node?.__typename === "GhostVault" &&
      isSameCanonicalAsset(
        edge.node.asset.metadata.symbol,
        asset.metadata.symbol
      )
  )?.node;
  if (vault?.__typename !== "GhostVault") return undefined;
  const symbol = canonicalAssetSymbol(vault.asset.metadata.symbol);
  return {
    kind: "lending",
    href: `/lend/${assetToLendUrlSegment(vault.asset)}`,
    title: t("transactionExploreLendingTitle", { symbol }),
    description: t("transactionExploreLendingDescription", { symbol }),
    apr:
      vault.status.apr.status === "AVAILABLE"
        ? displayApr(vault.status.apr.value)
        : undefined,
    aprLabel: "current",
  };
};

const stakingRecommendation = (
  symbol: string,
  data: TransferSuccessRecommendationsQuery["response"],
  t: Translate
): TransferSuccessRecommendation | undefined => {
  const isRune = symbol === "RUNE";
  const stakingSymbol = isRune ? "BRUNE" : symbol;
  const pool = data.staking?.pools.find(
    (candidate) =>
      candidate.bondAsset.metadata.symbol.toUpperCase() === stakingSymbol
  );
  if (!pool) return undefined;
  return {
    kind: "staking",
    href: stakeRoute(pool.bondAsset),
    title: isRune
      ? t("transactionExploreRuneStakingTitle")
      : t("transactionExploreStakingTitle", {
          symbol: pool.bondAsset.metadata.symbol,
        }),
    description: isRune
      ? t("transactionExploreRuneStakingDescription")
      : t("transactionExploreStakingDescription", {
          symbol: pool.bondAsset.metadata.symbol,
        }),
    apr:
      pool.summary.apr.status === "AVAILABLE"
        ? displayApr(pool.summary.apr.value)
        : undefined,
    aprLabel: "current",
  };
};

const borrowRecommendation = (
  asset: Asset,
  data: TransferSuccessRecommendationsQuery["response"],
  t: Translate
): TransferSuccessRecommendation | undefined => {
  const collateral = data.ghostCredit?.collaterals.find(
    (candidate) =>
      candidate.ratio > 0n &&
      isSameCanonicalAsset(
        candidate.asset.metadata.symbol,
        asset.metadata.symbol
      )
  );
  if (!collateral) return undefined;
  const vault = [...(data.ghostCredit?.vaults || [])]
    .filter((candidate) =>
      ["USDC", "USDT"].includes(candidate.borrower.asset.metadata.symbol)
    )
    .sort((left, right) => {
      const leftRate = left.borrower.vault.status.debtRate;
      const rightRate = right.borrower.vault.status.debtRate;
      return leftRate === rightRate ? 0 : leftRate < rightRate ? -1 : 1;
    })[0];
  if (!vault) return undefined;
  const borrowAsset = vault.borrower.asset.metadata.symbol;
  const symbol = canonicalAssetSymbol(asset.metadata.symbol);
  return {
    kind: "borrow",
    href: `/borrow/${assetToTradeUrlSegment(collateral.asset)}/${encodeURIComponent(borrowAsset)}`,
    title: t("transactionExploreBorrowTitle", { borrowAsset, symbol }),
    description: t("transactionExploreBorrowDescription", {
      borrowAsset,
      symbol,
    }),
    apr: displayApr(vault.borrower.vault.status.debtRate),
    aprLabel: "borrow",
  };
};

const tradeRecommendation = (
  symbol: string,
  t: Translate,
  market?: Market
): TransferSuccessRecommendation => {
  const pair = market?.node;
  return {
    kind: "trade",
    href: pair
      ? `/trade/${assetToTradeUrlSegment(pair.assetBase)}/${assetToTradeUrlSegment(pair.assetQuote)}`
      : "/trade",
    title: t("transactionExploreTradeTitle", { symbol }),
    description: t("transactionExploreTradeDescription", { symbol }),
  };
};

const rujiRecommendation = (
  data: TransferSuccessRecommendationsQuery["response"],
  t: Translate
): TransferSuccessRecommendation => {
  const pool = data.staking?.pools.find(
    (candidate) => candidate.bondAsset.metadata.symbol.toUpperCase() === "RUJI"
  );
  return {
    kind: "ruji",
    href: stakeRoute("RUJI"),
    title: t("transactionExploreRujiTitle"),
    description: t("transactionExploreRujiDescription"),
    apr:
      pool?.summary.apr.status === "AVAILABLE"
        ? displayApr(pool.summary.apr.value)
        : undefined,
    aprLabel: "current",
  };
};

const displayApr = (apr: bigint | null | undefined) =>
  apr != null && apr >= MIN_DISPLAY_APR ? formatBigintPercent(apr) : undefined;
